import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  type?: 'website' | 'article' | 'book';
  canonical?: string;
  noindex?: boolean;
}

const DEFAULT_TITLE = 'Leafnode - Your Personal Sci-Fi Library & Reading Companion';
const DEFAULT_DESCRIPTION = 'A library of science fiction characters and authors who answer in their own words. Speak with them, then read the book.';
// A new NAME, not just a new picture. The leaf replaced an AI-generated purple
// artwork under /og-image.jpg on 2026-07-18, and Bing went on showing the purple
// one as the search thumbnail for eight weeks: a search engine can keep a picture
// against its address, so a new picture at an old address looks unchanged. If
// this image ever changes, give the file a new name as well.
const DEFAULT_IMAGE = '/og-image-leaf.jpg';
// Fixed, not window.location.origin: a visit on the apex domain or a preview
// deploy would otherwise self-canonicalise to that origin, splitting ranking
// signals away from the canonical www host.
const SITE_URL = 'https://www.leafnode.co.uk';
// Hoisted so the effect's dependency array gets a stable reference — an inline
// default array is a new value every render, re-running the effect each time.
const DEFAULT_KEYWORDS = ['science fiction', 'books', 'reading tracker', 'sci-fi', 'library', 'book recommendations'];

export function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  image = DEFAULT_IMAGE,
  type = 'website',
  canonical,
  noindex = false,
}: SEOHeadProps) {
  const location = useLocation();
  
  // Only append the site suffix when the page hasn't already included it,
  // otherwise titles double up ("… | Leafnode | Leafnode").
  const fullTitle = title
    ? (/leafnode/i.test(title) ? title : `${title} | Leafnode`)
    : DEFAULT_TITLE;
  const fullImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  const fullCanonical = canonical || `${SITE_URL}${location.pathname}`;

  useEffect(() => {
    // Update title
    document.title = fullTitle;

    // Update or create meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords.join(', '));
    
    // Open Graph
    updateMetaTag('og:title', fullTitle, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:type', type, 'property');
    updateMetaTag('og:url', fullCanonical, 'property');
    updateMetaTag('og:image', fullImage, 'property');
    updateMetaTag('og:site_name', 'Leafnode', 'property');

    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image', 'name');
    updateMetaTag('twitter:title', fullTitle, 'name');
    updateMetaTag('twitter:description', description, 'name');
    updateMetaTag('twitter:image', fullImage, 'name');

    // Canonical link
    updateLink('canonical', fullCanonical);

    // Robots
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }
  }, [fullTitle, description, keywords, fullImage, type, fullCanonical, noindex]);

  return null;
}

function updateMetaTag(name: string, content: string, attribute: 'name' | 'property' = 'name') {
  let element = document.querySelector(`meta[${attribute}="${name}"]`);
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', content);
}

function updateLink(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  
  element.href = href;
}
